// src/pages/LandingPage.tsx
import React from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cosloHero from "../assets/coslo-hero.png";
import cosloAssist247 from "../assets/coslo-assist-247.png";
import cosloGrowth from "../assets/coslo-growth.png";
import SiteFooter from "../components/SiteFooter";
import LanguageSwitcher from "../components/LanguageSwitcher";

const LandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const bot = searchParams.get("bot");
  const { t } = useTranslation();

  // Mantiene il comportamento originale: /?bot=slug → /demo/:slug
  if (bot) {
    return <Navigate to={`/demo/${bot}`} replace />;
  }

  return (
    <div className="landing-page">
      {/* Language switcher bar */}
      <div className="landing-lang-bar">
        <LanguageSwitcher />
      </div>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-container">
          <div className="lp-hero-grid">
            {/* Colonna testo */}
            <div className="lp-hero-content">
              <h1 className="lp-hero-title">
                {t("landing.hero.title")}
              </h1>
              <p className="lp-hero-subtitle">
                {t("landing.hero.subtitle")}
              </p>
              <div className="lp-hero-cta">
                <Link to="/register" className="lp-btn lp-btn-primary">
                  {t("landing.hero.ctaPrimary")}
                </Link>
                <Link to="/login" className="lp-btn lp-btn-secondary">
                  {t("landing.hero.ctaSecondary")}
                </Link>
              </div>
              <p className="lp-hero-note">
                {t("landing.hero.note")}
              </p>
            </div>

            {/* Colonna Coslo */}
            <div className="lp-hero-image">
              <div className="lp-hero-mascotte-frame">
                <div className="lp-hero-mascotte-glow" />
                <img
                  src={cosloHero}
                  alt={t("landing.hero.altHero")}
                  className="lp-hero-mascotte-img"
                />
                <div className="lp-hero-mascotte-badge">
                  <span className="lp-hero-mascotte-dot" />
                  {t("landing.hero.badge")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="lp-section">
        <div className="lp-container lp-intro-with-image">
          {/* 1. Titolo */}
          <div className="lp-intro-heading">
            <h2 className="lp-section-title">
              {t("landing.intro.title")}
            </h2>
          </div>

          {/* 2. Immagine Coslo 24/7 */}
          <div className="lp-intro-image">
            <div className="lp-intro-illustration-frame">
              <img
                src={cosloAssist247}
                alt={t("landing.intro.imageAlt")}
              />
            </div>
          </div>

          {/* 3. Corpo testo */}
          <div className="lp-intro-body">
            <p className="lp-text">
              {t("landing.intro.p1")}
            </p>
            <p className="lp-text">
              {t("landing.intro.p2")}
            </p>
            <p className="lp-text">
              {t("landing.intro.p3")}
            </p>
          </div>
        </div>
      </section>

      {/* MULTICANALE */}
      <section className="lp-section lp-section-alt">
        <div className="lp-container">
          <div className="lp-grid-2 lp-section-intro">
            <div>
              <h2 className="lp-section-title">
                {t("landing.multichannel.title")}
              </h2>
            </div>
            <div>
              <p className="lp-text">
                {t("landing.multichannel.p1")}
              </p>
              <p className="lp-text">
                {t("landing.multichannel.p2")}
              </p>
            </div>
          </div>

          <div className="lp-grid-cards">
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.multichannel.cards.webWidget.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.multichannel.cards.webWidget.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.multichannel.cards.whatsapp.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.multichannel.cards.whatsapp.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.multichannel.cards.messenger.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.multichannel.cards.messenger.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.multichannel.cards.instagram.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.multichannel.cards.instagram.text")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONOSCENZA CONTENUTI */}
      <section className="lp-section">
        <div className="lp-container lp-grid-2">
          <div className="lp-knowledge-left">
            <h2 className="lp-section-title">
              {t("landing.knowledge.title")}
            </h2>

            <div className="lp-knowledge-card">
              <div className="lp-knowledge-avatar">
                <img
                  src={cosloHero}
                  alt={t("landing.knowledge.imageAlt")}
                />
              </div>

              <div className="lp-knowledge-doc lp-knowledge-doc-1">
                <span className="lp-knowledge-doc-label">
                  {t("landing.knowledge.labels.pdf")}
                </span>
              </div>
              <div className="lp-knowledge-doc lp-knowledge-doc-2">
                <span className="lp-knowledge-doc-label">
                  {t("landing.knowledge.labels.site")}
                </span>
              </div>
              <div className="lp-knowledge-doc lp-knowledge-doc-3">
                <span className="lp-knowledge-doc-label">
                  {t("landing.knowledge.labels.faq")}
                </span>
              </div>
            </div>
          </div>
          <div>
            <p className="lp-text">
              {t("landing.knowledge.p1")}
            </p>
            <p className="lp-text">
              {t("landing.knowledge.p2")}
            </p>
            <ul className="lp-list">
              <li>{t("landing.knowledge.list.l1")}</li>
              <li>{t("landing.knowledge.list.l2")}</li>
              <li>{t("landing.knowledge.list.l3")}</li>
              <li>{t("landing.knowledge.list.l4")}</li>
            </ul>
            <p className="lp-text">
              {t("landing.knowledge.p3")}
            </p>
            <p className="lp-text">
              {t("landing.knowledge.p4")}
            </p>
          </div>
        </div>
      </section>

      {/* PRENOTAZIONI */}
      <section className="lp-section lp-section-alt">
        <div className="lp-container lp-grid-2 lp-section-calendar">
          <div>
            <h2 className="lp-section-title">
              {t("landing.booking.title")}
            </h2>
            <p className="lp-text">
              {t("landing.booking.p1")}
            </p>
            <p className="lp-text">
              {t("landing.booking.p2")}
            </p>
            <ul className="lp-list">
              <li>{t("landing.booking.list.l1")}</li>
              <li>{t("landing.booking.list.l2")}</li>
              <li>{t("landing.booking.list.l3")}</li>
              <li>{t("landing.booking.list.l4")}</li>
            </ul>
            <p className="lp-text">
              {t("landing.booking.p3")}
            </p>
          </div>
          <div className="lp-calendar-mock">
            <div className="lp-calendar-header">
              {t("landing.booking.calendar.header")}
            </div>
            <div className="lp-calendar-body">
              <div className="lp-calendar-event">
                <div className="lp-event-time">
                  {t("landing.booking.calendar.event1.time")}
                </div>
                <div className="lp-event-details">
                  <strong>
                    {t("landing.booking.calendar.event1.title")}
                  </strong>
                  <span>
                    {t("landing.booking.calendar.event1.subtitle")}
                  </span>
                </div>
              </div>
              <div className="lp-calendar-event">
                <div className="lp-event-time">
                  {t("landing.booking.calendar.event2.time")}
                </div>
                <div className="lp-event-details">
                  <strong>
                    {t("landing.booking.calendar.event2.title")}
                  </strong>
                  <span>
                    {t("landing.booking.calendar.event2.subtitle")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CRESCITA */}
      <section className="lp-section">
        <div className="lp-container lp-growth-grid">
          {/* Coslo che costruisce i blocchi */}
          <div className="lp-growth-visual">
            <div className="lp-growth-card">
              <img
                src={cosloGrowth}
                alt={t("landing.growth.imageAlt")}
                className="lp-growth-img"
              />
            </div>
          </div>

          {/* Testo + pillole */}
          <div>
            <h2 className="lp-section-title">
              {t("landing.growth.title")}
            </h2>
            <p className="lp-text">
              {t("landing.growth.p1")}
            </p>
            <p className="lp-text">
              {t("landing.growth.p2")}
            </p>
            <div className="lp-pills">
              <span className="lp-pill">
                {t("landing.growth.pills.p1")}
              </span>
              <span className="lp-pill">
                {t("landing.growth.pills.p2")}
              </span>
              <span className="lp-pill">
                {t("landing.growth.pills.p3")}
              </span>
              <span className="lp-pill">
                {t("landing.growth.pills.p4")}
              </span>
              <span className="lp-pill">
                {t("landing.growth.pills.p5")}
              </span>
              <span className="lp-pill">
                {t("landing.growth.pills.p6")}
              </span>
            </div>
            <p className="lp-text">
              {t("landing.growth.p3")}
            </p>
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <section className="lp-section lp-section-alt lp-section-benefits">
        <div className="lp-container">
          <h2 className="lp-section-title lp-centered">
            {t("landing.benefits.title")}
          </h2>
          <div className="lp-grid-cards lp-grid-3">
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.benefits.cards.c1.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.benefits.cards.c1.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.benefits.cards.c2.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.benefits.cards.c2.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.benefits.cards.c3.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.benefits.cards.c3.text")}
              </p>
            </div>
            <div className="lp-card">
              <h3 className="lp-card-title">
                {t("landing.benefits.cards.c4.title")}
              </h3>
              <p className="lp-card-text">
                {t("landing.benefits.cards.c4.text")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MINI COSLO CHAT */}
      <section className="lp-section">
        <div className="lp-container lp-mini-coslo-grid">
          <div>
            <h2 className="lp-section-title">
              {t("landing.miniCoslo.title")}
            </h2>
            <p className="lp-text">
              {t("landing.miniCoslo.p1")}
            </p>
            <p className="lp-text">
              {t("landing.miniCoslo.p2")}
            </p>
          </div>

          <div className="lp-mini-coslo-widget">
            <div className="lp-mini-coslo-avatar">
              <img
                src={cosloHero}
                alt={t("landing.miniCoslo.imageAlt")}
              />
            </div>

            <div className="lp-mini-coslo-bubbles">
              <div className="lp-mini-coslo-bubble lp-mini-coslo-bubble-user">
                {t("landing.miniCoslo.bubbleUser")}
              </div>
              <div className="lp-mini-coslo-bubble lp-mini-coslo-bubble-bot">
                {t("landing.miniCoslo.bubbleBot")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="lp-section lp-cta-final">
        <div className="lp-container">
          <h2 className="lp-section-title lp-centered">
            {t("landing.cta.title")}
          </h2>
          <p className="lp-text lp-centered lp-cta-text">
            {t("landing.cta.text")}
          </p>
          <div className="lp-hero-cta">
            <Link to="/register" className="lp-btn lp-btn-primary">
              {t("landing.cta.primaryCta")}
            </Link>
            <Link to="/login" className="lp-btn lp-btn-secondary">
              {t("landing.cta.secondaryCta")}
            </Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
